import React, { createContext, useState, useCallback, useContext, ReactNode } from 'react';
import { Toast, ToastProps } from '../components/ui/Toast';

interface ToastContextType {
  showToast: (message: string, typeOrDuration?: 'success' | 'error' | 'info' | number, duration?: number) => void;
}

type ToastState = Omit<ToastProps, 'onDismiss'>;

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const showToast = useCallback((message: string, typeOrDuration?: 'success' | 'error' | 'info' | number, duration?: number) => {
    let id;
    try {
        id = crypto.randomUUID();
    } catch (e) {
        id = Math.random().toString(36).substring(2, 9);
    }
    
    let toastType: 'success' | 'error' | 'info' = 'info';
    let toastDuration = 5000;

    if (typeof typeOrDuration === 'string') {
        toastType = typeOrDuration;
        if (duration) toastDuration = duration;
    } else if (typeof typeOrDuration === 'number') {
        toastDuration = typeOrDuration;
    }

    setToasts(prevToasts => [...prevToasts, { id, message, type: toastType, duration: toastDuration }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-5 right-5 space-y-2 z-[100]">
        {toasts.map(toast => (
          <Toast key={toast.id} {...toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
